import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Megaphone, Paperclip, ArrowLeft, Loader2, ExternalLink, Download } from "lucide-react";
import { api } from "../lib/api";

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("it-IT", {
            day: "2-digit",
            month: "long",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
};

// Costruisce URL allegato con token come query param (auth backend supporta entrambi)
const makeAttachmentUrl = (raw) => {
    if (!raw) return "";
    const token =
        (typeof localStorage !== "undefined" && localStorage.getItem("token")) || "";
    const base = process.env.REACT_APP_BACKEND_URL || "";
    let abs = raw;
    if (raw.startsWith("/")) abs = base.replace(/\/$/, "") + raw;
    if (token) abs += (abs.includes("?") ? "&" : "?") + "token=" + encodeURIComponent(token);
    return abs;
};

export default function CommunicationDetail() {
    const { id } = useParams();
    const [comm, setComm] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.get(`/communications/${id}`)
            .then(({ data }) => setComm(data))
            .catch((e) => {
                console.error("Load communication failed:", e);
                setError(e.response?.data?.detail || "Comunicazione non trovata");
            })
            .finally(() => setLoading(false));
    }, [id]);

    if (loading) {
        return (
            <div className="max-w-3xl mx-auto card p-10 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-[#0C4650]" />
            </div>
        );
    }
    if (error || !comm) {
        return (
            <div className="max-w-3xl mx-auto card p-10 text-center"
                 data-testid="communication-error">
                <h2 className="text-lg font-black text-[#0C4650]">
                    Comunicazione non disponibile
                </h2>
                <p className="text-sm text-gray-500 mt-1">{error || "Non trovata"}</p>
                <Link to="/comunicazioni"
                      className="inline-flex items-center gap-1 mt-4 text-sm font-bold text-[#0C4650]">
                    <ArrowLeft className="w-4 h-4" /> Torna a Comunicazioni
                </Link>
            </div>
        );
    }

    // Render paragrafi mantenendo righe vuote come separatori
    const paragraphs = (comm.message || "").split(/\n\n+/).filter(Boolean);

    const ctaUrl = comm.cta_url || "";
    let ctaResolved = ctaUrl;
    if (ctaUrl.startsWith("/")) {
        ctaResolved = ctaUrl; // route React interna
    }

    return (
        <div className="max-w-3xl mx-auto" data-testid="communication-detail-page">
            <Link to="/comunicazioni"
                  className="inline-flex items-center gap-1 text-sm font-bold text-[#0C4650] mb-4 hover:underline"
                  data-testid="back-to-communications">
                <ArrowLeft className="w-4 h-4" /> Tutte le comunicazioni
            </Link>

            <div className="card p-6 sm:p-8" data-testid="communication-content">
                <div className="flex items-start gap-4 mb-5">
                    <div className="w-12 h-12 shrink-0 rounded-xl bg-[#C7FF2C] flex items-center justify-center text-[#0C4650]">
                        <Megaphone className="w-6 h-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] tracking-widest font-bold text-[#898A8D]">
                            COMUNICAZIONE · {fmtDate(comm.created_at)}
                        </div>
                        <h1 className="text-2xl sm:text-3xl font-black text-[#0C4650] mt-1 break-words">
                            {comm.subject}
                        </h1>
                    </div>
                </div>

                <div className="prose max-w-none text-gray-800 leading-relaxed"
                     data-testid="communication-message">
                    {paragraphs.length === 0 ? (
                        <p className="text-gray-500">(messaggio vuoto)</p>
                    ) : paragraphs.map((p, i) => (
                        <p key={i} className="mb-3 whitespace-pre-wrap break-words">{p}</p>
                    ))}
                </div>

                {comm.attachment_url && (
                    <div className="mt-6 p-4 bg-[#C7FF2C]/20 border-2 border-[#C7FF2C] rounded-xl"
                         data-testid="communication-attachment">
                        <div className="text-[10px] tracking-widest font-bold text-[#0C4650] mb-2">
                            📎 ALLEGATO
                        </div>
                        <a
                            href={makeAttachmentUrl(comm.attachment_url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 bg-[#0C4650] text-white font-bold px-4 py-2.5 rounded-lg hover:bg-[#0a3a42] transition-all break-all"
                            data-testid="download-attachment-btn"
                        >
                            <Download className="w-4 h-4 shrink-0" />
                            {comm.attachment_name || "Scarica il file"}
                        </a>
                    </div>
                )}

                {comm.cta_url && (
                    <div className="mt-6">
                        {ctaUrl.startsWith("http") ? (
                            <a href={ctaResolved}
                               target="_blank"
                               rel="noopener noreferrer"
                               className="btn-primary inline-flex"
                               data-testid="communication-cta-btn">
                                <ExternalLink className="w-4 h-4" />
                                {comm.cta_label || "Apri il link"}
                            </a>
                        ) : (
                            <Link to={ctaResolved}
                                  className="btn-primary inline-flex"
                                  data-testid="communication-cta-btn">
                                {comm.cta_label || "Apri"}
                            </Link>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
